import { useNavigate, useLocation } from "react-router-dom";
import { useCallback } from "react";

/**
 * Custom hook that provides navigation utilities with route parameter replacement
 *
 * @returns Object containing handleNavigation and isActive functions
 */
export function useNavigation() {
  const navigate = useNavigate();
  const location = useLocation();

  /**
   * Navigates to a route after replacing route parameters with actual values
   *
   * @param to - Route template (e.g., "/facilities/:facilityId/details")
   * @param params - Parameters to replace in the route (e.g., { facilityId: "123" })
   */
  const handleNavigation = useCallback(
    (to: string, params: Record<string, string> = {}) => {
      // Replace route parameters with actual values
      let route = to;
      Object.entries(params).forEach(([key, value]) => {
        route = route.replace(`:${key}`, value);
      });
      navigate(route);
    },
    [navigate]
  );

  /**
   * Checks if the current location is active for the given route
   *
   * @param to - Route template (e.g., "/facilities/:facilityId/details")
   * @param params - Parameters to replace in the route (e.g., { facilityId: "123" })
   * @returns Boolean indicating if the current location starts with the resolved route
   */
  const isActive = useCallback(
    (to: string, params: Record<string, string> = {}) => {
      let route = to;
      Object.entries(params).forEach(([key, value]) => {
        route = route.replace(`:${key}`, value);
      });
      return location.pathname.startsWith(route);
    },
    [location.pathname]
  );

  return {
    handleNavigation,
    isActive
  };
}
